<template>
  <div class="row">

    <div class="col-12 px-0">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-hinzufuegen" aria-hidden="true"></span>
        {{ $t('views.roomAdd.title') }}
      </h1>
    </div>

    <link-back to="/rooms" i18nMessage="common.backToOverview" class="mb-3" />

    <loading-container :state="state" errorLinkTo="/rooms">
      <form @submit.prevent="onSubmit()">
        <control-input
          i18nLabel="common.room.name"
          :validation="v$.name"
          v-model="name"
        />
        <control-input
          i18nLabel="common.room.description"
          :validation="v$.description"
          v-model="description"
        />

        <h2 class="h2-title mt-3">{{ $t('common.room.capacity') }}</h2>

        <div class="col-12 d-flex bg-lightblue p-3 kapazitaetrow mt-3">
          <control-integer
            i18nLabel="common.room.personCount"
            :validation="v$.capacity"
            v-model="capacity"
            :showBreak="true"
            class=" d-flex flex-wrap align-items-center mr-3"
          />
        </div>

        <h2 class="h2-title mt-3">{{ $t('common.room.reservationPeriod') }}</h2>

        <div class="col-12 d-flex align-items-center bg-lightblue p-3 kapazitaetrow mt-3">
          <control-integer
            i18nLabel="common.room.openStartValue"
            :validation="v$.openStartValue"
            v-model="openStartValue"
            :showBreak="true"
            class=" d-flex flex-wrap align-items-center mr-3"
          />
          <control-select
            i18nLabel="common.room.openStartType"
            :validation="v$.openStartType"
            :labelHidden="true"
            v-model="openStartType"
            :availableValues="selectOptions()"
          />
          <span class="ml-3">{{ $t('common.room.additionalStartEndInfo') }}</span>
        </div>

        <div class="col-12 d-flex align-items-center bg-lightblue p-3 kapazitaetrow mt-3">
          <control-integer
            i18nLabel="common.room.openEndValue"
            :validation="v$.openEndValue"
            v-model="openEndValue"
            :showBreak="true"
            class=" d-flex flex-wrap align-items-center mr-3"
          />
          <control-select
            i18nLabel="common.room.openEndType"
            :validation="v$.openEndType"
            :labelHidden="true"
            v-model="openEndType"
            :availableValues="selectOptions()"
          />
          <span class="ml-3">{{ $t('common.room.additionalStartEndInfo') }}</span>
        </div>

        <div class="row mx-auto mt-4">
          <div class="col-12 col-md-4 offset-md-2 col-lg-3 offset-lg-3 mt-1 px-xs-0">
          <button-cancel to="/rooms" class="btn-w-100" />
          </div>
          <div class="col-12 col-md-4 col-lg-3 mt-1 px-xs-0">
          <button-save i18nMessage="common.room.add" class="btn-w-100" />
          </div>
        </div>

      </form>
    </loading-container>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import LoadingMixin from '@/mixins/LoadingMixin'
import { useVuelidate } from '@vuelidate/core'
import { validateAndFocusInvalid } from '@/utils/formUtils'
import { createRoomValidations } from '@/utils/validationUtils'
import { parseDayPeriodTypeEnum } from '@/utils/apiUtils'
import config from '@/config/config'

import LoadingContainer from '@/components/structure/LoadingContainer.vue'
import ControlInput from '@/components/controls/ControlInput.vue'
import ControlInteger from '@/components/controls/ControlInteger.vue'
import { LoadingState } from '@/utils/loadingUtils'
import ButtonSave from '@/components/interaction/ButtonSave.vue'
import ButtonCancel from '@/components/interaction/ButtonCancel.vue'
import { adminService } from '@/services/adminService'
import { CreateRoom, DayPeriodTypeEnum } from '@/generated/openapi'
import ControlSelect from '@/components/controls/ControlSelect.vue'
import { SelectOption } from '@/models/SelectOption'
import LinkBack from '@/components/interaction/LinkBack.vue'

export default defineComponent({
  name: 'RoomAdd',
  mixins: [
    LoadingMixin
  ],
  components: {
    LoadingContainer,
    ControlInput,
    ControlInteger,
    ButtonSave,
    ButtonCancel,
    ControlSelect,
    LinkBack
  },
  created() {
    this.state = LoadingState.success;
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      name: '',
      description: '',
      capacity: 0,
      openStartType: DayPeriodTypeEnum.Day as string,
      openStartValue: config.timeslotBookableStartValue,
      openEndType: DayPeriodTypeEnum.WorkingDay as string,
      openEndValue: config.timeslotBookableEndValue
    }
  },
  validations() {
    return createRoomValidations()
  },
  methods: {
    selectOptions(): SelectOption[] {
      return [
        DayPeriodTypeEnum.WorkingDay,
        DayPeriodTypeEnum.Day
      ].map(dayType => ({
        value: dayType,
        label: this.$t('common.dayTypes.' + dayType)
      }))
    },
    asCreateRoom(): CreateRoom {
      return {
        name: this.name,
        description: this.description,
        capacity: this.capacity,
        openStart: {
          type: parseDayPeriodTypeEnum(this.openStartType),
          value: this.openStartValue
        },
        openEnd: {
          type: parseDayPeriodTypeEnum(this.openEndType),
          value: this.openEndValue
        }
      }
    },
    onSubmit() {
      validateAndFocusInvalid(this.v$, this.$el).then(this.createRoom)
    },
    createRoom() {
      this.state = LoadingState.loading;
      adminService.createRoom(this.asCreateRoom()).then(() => {
        this.$router.push('/rooms')
      }).catch(() => {
        this.state = LoadingState.error;
      })
    }
  }
});
</script>
